import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import DashboardFooter from '../components/DashboardFooter';

const API        = `${process.env.REACT_APP_HOST_API_URL}/admin-user/blogs/`;
const IMAGE_API  = `${process.env.REACT_APP_HOST_API_URL}/admin-user/blogs/upload-image/`;

const emptyForm = {
    title: '',
    content: '',
    thumbnail: null,
    videoUrl: '',
    shortDescription: '',
    metaTitle: '',
    metaDescription: '',
    isPublished: true,
};

const AuthBlog = () => {

    const [vlogs, setVlogs]               = useState([]);
    const [loading, setLoading]           = useState(false);
    const [saving, setSaving]             = useState(false);
    const [imageUploading, setImageUploading] = useState(false);

    // 'list' | 'add' | 'edit'
    const [mode, setMode]             = useState('list');
    const [editId, setEditId]         = useState(null);

    const quillRef = useRef(null);

    // Search
    const [searchTitle, setSearchTitle] = useState('');
    const [searchDate, setSearchDate]   = useState('');

    // Form
    const [form, setForm]             = useState(emptyForm);
    // preview of existing thumbnail when editing
    const [thumbPreview, setThumbPreview] = useState(null);

    useEffect(() => { fetchVlogs(); }, []);

    /* ─── Fetch ─────────────────────────────────────────────── */
    const fetchVlogs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                    setLoading(false);
                    return;
                }
            const res = await axios.get(API, {
            headers: {
                Authorization: `Bearer ${token}`,
                   'Content-Type': 'application/json'
                 

            }
        })
            // const res = await axios.get(API);
            setVlogs(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    /* ─── Quill image upload handler ────────────────────────── */
    const imageHandler = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const fd = new FormData();
            fd.append('image', file);

            try {
                setImageUploading(true);
                const token = localStorage.getItem('token');
                if (!token) {
                        setImageUploading(false);
                        return;
                    }

                const res = await axios.post(IMAGE_API, fd, {
                    headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
                });

                // Insert image URL into editor at current cursor position
                const quill = quillRef.current.getEditor();
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', res.data.url);
                quill.setSelection(range.index + 1); // move cursor after image

            } catch (e) {
                console.error('Image upload failed:', e);
                alert('Image upload failed. Check console.');
            } finally {
                setImageUploading(false);
            }
        };
    }, []);

    /* ─── Quill toolbar & formats config ────────────────────── */
    const quillModules = {
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ align: [] }],
                ['blockquote', 'code-block'],
                ['link', 'image', 'video'],   // ← image button here
                ['clean'],
            ],
            handlers: {
                image: imageHandler,           // ← our custom handler
            },
        },
    };

    const quillFormats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'color', 'background', 'list', 'bullet', 'align',
        'blockquote', 'code-block', 'link', 'image', 'video',
    ];


    const openEdit = (vlog) => {
        setEditId(vlog.id);
        setForm({
            title:            vlog.title            || '',
            content:          vlog.content          || '',
            thumbnail:        null,                        // new file only
            videoUrl:         vlog.video_url        || '',
            shortDescription: vlog.short_description|| '',
            metaTitle:        vlog.meta_title       || '',
            metaDescription:  vlog.meta_description || '',
            isPublished:      vlog.is_published     ?? true,
        });
        setThumbPreview(vlog.thumbnail || null);
        setMode('edit');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /* ─── Open add ───────────────────────────────────────────── */
    const openAdd = () => {
        setEditId(null);
        setForm(emptyForm);
        setThumbPreview(null);
        setMode('add');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /* ─── Save (create or update) ────────────────────────────── */
    const saveBlog = async () => {
        if (!form.title.trim()) {
            alert('Blog title is required.');
            return;
        }

        const fd = new FormData();
        fd.append('title',             form.title);
        fd.append('content',           form.content);
        fd.append('video_url',         form.videoUrl);
        fd.append('short_description', form.shortDescription);
        fd.append('meta_title',        form.metaTitle);
        fd.append('meta_description',  form.metaDescription);
        fd.append('is_published',      form.isPublished);
        if (form.thumbnail) {
            fd.append('thumbnail', form.thumbnail);
        }
        const token = localStorage.getItem('token');
          
        const headers = { 'Content-Type': 'multipart/form-data',   Authorization: `Bearer ${token}` };

        try {
            setSaving(true);
            if (mode === 'edit') {
                await axios.patch(`${API}${editId}/`, fd, { headers });
            } else {
                await axios.post(API, fd, { headers });
            }
            await fetchVlogs();
            setMode('list');
        } catch (e) {
            console.error(e);
            alert('Something went wrong. Check console.');
        } finally {
            setSaving(false);
        }
    };

    /* ─── Helpers ────────────────────────────────────────────── */
    const set = (key) => (e) =>
        setForm((p) => ({ ...p, [key]: e.target.value }));

    const filteredBlogs = vlogs.filter((v) => {
        const matchTitle = v.title?.toLowerCase().includes(searchTitle.toLowerCase());
        const matchDate  = !searchDate || v.created_at?.slice(0, 10) === searchDate;
        return matchTitle && matchDate;
    });

    /* ─── Shared form UI ─────────────────────────────────────── */
    const renderForm = () => (
        <div className=" border-0 shadow rounded-4 mx-auto mb-5 max-w-full">
            <div className="card-body p-4 p-md-5">

                {/* Form Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="fw-bold mb-0">
                        {mode === 'edit' ? '✏️ Edit Blog' : '➕ Add Blog'}
                    </h4>
                    <button className="btn-close" onClick={() => setMode('list')} />
                </div>

                <div className="row g-4">

                    {/* Title */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">
                            Blog Title <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control form-control-lg"
                            placeholder="Enter blog title"
                            value={form.title}
                            onChange={set('title')}
                        />
                    </div>

                    {/* Youtube URL */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">YouTube URL</label>
                        <input
                            type="url"
                            className="form-control form-control-lg"
                            placeholder="https://youtube.com/watch?v=..."
                            value={form.videoUrl}
                            onChange={set('videoUrl')}
                        />
                    </div>

                    {/* Short Description */}
                    <div className="col-12">
                        <label className="form-label fw-semibold">Short Description</label>
                        <textarea
                            className="form-control"
                            rows={2}
                            placeholder="Brief summary shown in listings..."
                            value={form.shortDescription}
                            onChange={set('shortDescription')}
                        />
                    </div>

                    {/* Thumbnail */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Thumbnail</label>
                        {thumbPreview && (
                            <div className="mb-2">
                                <img
                                    src={thumbPreview}
                                    alt="Current thumbnail"
                                    style={{
                                        height: '90px',
                                        borderRadius: '8px',
                                        objectFit: 'cover',
                                        border: '1px solid #dee2e6'
                                    }}
                                />
                                <small className="d-block text-muted mt-1">
                                    Current thumbnail — upload a new file to replace
                                </small>
                            </div>
                        )}
                        <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                setForm((p) => ({ ...p, thumbnail: file }));
                                if (file) setThumbPreview(URL.createObjectURL(file));
                            }}
                        />
                    </div>

                    {/* Is Published */}
                    <div className="col-md-6 d-flex align-items-end pb-2">
                        <div className="form-check form-switch">
                            <input
                                type="checkbox"
                                role="switch"
                                className="form-check-input"
                                id="isPublished"
                                style={{ width: '2.5rem', height: '1.4rem' }}
                                checked={form.isPublished}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, isPublished: e.target.checked }))
                                }
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor="isPublished">
                                Published
                            </label>
                        </div>
                    </div>

                    {/* SEO: Meta Title */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Meta Title <small className="text-muted fw-normal">(SEO)</small></label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="SEO meta title"
                            value={form.metaTitle}
                            onChange={set('metaTitle')}
                        />
                    </div>

                    {/* SEO: Meta Description */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Meta Description <small className="text-muted fw-normal">(SEO)</small></label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="SEO meta description"
                            value={form.metaDescription}
                            onChange={set('metaDescription')}
                        />
                    </div>

                    {/* Rich Text Content */}
                    <div className="col-12">
                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                            Blog Content
                            {imageUploading && (
                                <span className="text-primary small d-flex align-items-center gap-1">
                                    <span className="spinner-border spinner-border-sm" />
                                    Uploading image…
                                </span>
                            )}
                        </label>
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={form.content}
                            onChange={(val) => setForm((p) => ({ ...p, content: val }))}
                            modules={quillModules}
                            formats={quillFormats}
                            style={{ height: '600px', marginBottom: '60px' }}
                        />
                    </div>

                    {/* Actions */}
                    <div className="col-12 d-flex gap-3">
                        <button
                            className="btn btn-primary btn-lg px-5"
                            onClick={saveBlog}
                            disabled={saving}
                        >
                            {saving
                                ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                                : (mode === 'edit' ? 'Update Blog' : 'Save Blog')
                            }
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-lg"
                            onClick={() => setMode('list')}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );

    /* ─── Render ─────────────────────────────────────────────── */
    return (
        <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: '#f5f7fb' }}>

            <div className="container-fluid py-3 px-md-4">

                {/* ── Header ── */}
                <div
                    className="shadow rounded-4 p-4 mb-4"
                    style={{ background: 'linear-gradient(135deg, #0564bf, #6ea8fe)' }}
                >
                    <h2 className="text-white fw-bold mb-1">Blog Management</h2>
                    <p className="text-white-50 mb-4">
                        {vlogs.length} blog{vlogs.length !== 1 ? 's' : ''} total
                    </p>

                    <div className="d-flex flex-wrap gap-3 align-items-center">
                        <input
                            type="date"
                            className="form-control"
                            style={{ maxWidth: '180px' }}
                            value={searchDate}
                            onChange={(e) => setSearchDate(e.target.value)}
                        />
                        <input
                            type="search"
                            placeholder="Search by title…"
                            className="form-control"
                            style={{ maxWidth: '280px' }}
                            value={searchTitle}
                            onChange={(e) => setSearchTitle(e.target.value)}
                        />
                        <button className="btn btn-light fw-semibold" onClick={openAdd}>
                            + Add Blog
                        </button>
                        <button className="btn btn-dark" onClick={() => setMode('list')}>
                            List All
                        </button>
                    </div>
                </div>

                {/* ── Add / Edit Form ── */}
                {(mode === 'add' || mode === 'edit') && renderForm()}

                {/* ── Blog Cards ── */}
                {mode === 'list' && (
                    loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" />
                        </div>
                    ) : filteredBlogs.length === 0 ? (
                        <div className="text-center py-5 text-muted">No blogs found.</div>
                    ) : (
                        <div className="row">
                            {filteredBlogs.map((vlog) => (
                                <div className="col-md-6 col-lg-4 mb-4" key={vlog.id}>
                                    <div className="card border-0 shadow-sm rounded-4 h-100"   style={{ padding: "7px" }}> 
                                        
 <span
                                                    className={`badge ms-2 mb-1 flex-shrink-0 ${
                                                        vlog.is_published ? 'bg-success' : 'bg-secondary'
                                                    }`}
                                                >
                                                    {vlog.is_published ? 'Published' : 'Draft'}
                                                </span>
                                        {vlog.thumbnail && (
                                            <img
                                                src={vlog.thumbnail}
                                                alt={vlog.title}
                                                className="card-img-top rounded-top-4"
                                                style={{ height: '200px', objectFit: 'cover' }}
                                            />
                                        )}

                                        <div className="card-body d-flex flex-column">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h5 className="fw-bold mb-0">{vlog.title}</h5>
                                                {/* <span
                                                    className={`badge ms-2 flex-shrink-0 ${
                                                        vlog.is_published ? 'bg-success' : 'bg-secondary'
                                                    }`}
                                                >
                                                    {vlog.is_published ? 'Published' : 'Draft'}
                                                </span> */}
                                            </div>

                                            {vlog.short_description && (
                                                <p className="text-muted small mb-2" style={{ lineHeight: 1.4 }}>
                                                    {vlog.short_description.slice(0, 120)}
                                                    {vlog.short_description.length > 120 ? '…' : ''}
                                                </p>
                                            )}

                                            {/* <div
                                                className="text-muted small mt-1"
                                                style={{ maxHeight: '80px', overflow: 'hidden' }}
                                                dangerouslySetInnerHTML={{ __html: vlog.content }}
                                            /> */}

                                            {vlog.video_url && (
                                                <div className="mt-3">
                                                    <iframe
                                                        width="100%"
                                                        height="180"
                                                        src={vlog.video_url.replace('watch?v=', 'embed/')}
                                                        title="YouTube"
                                                        frameBorder="0"
                                                        allowFullScreen
                                                    />
                                                </div>
                                            )}

                                            <div className="mt-auto pt-3 d-flex justify-content-between align-items-center border-top">
                                                <small className="text-muted">
                                                    {new Date(vlog.created_at).toLocaleDateString('en-IN', {
                                                        day: 'numeric', month: 'short', year: 'numeric'
                                                    })}
                                                </small>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => openEdit(vlog)}
                                                >
                                                    ✏️ Edit
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}

            </div>

            <DashboardFooter />
        </div>
    );
};

export default AuthBlog;
